import React, { useEffect, useState, useRef } from 'react'
import PropTypes from 'prop-types'
import { Dialog, DialogContent } from '@material-ui/core'
import { useIntl } from 'react-intl'
import SelectGroupDialogHeader from './components/SelectGroupDialogHeader'
import SelectGroupDialogFooter from './components/SelectGroupDialogFooter'
import clsx from 'clsx'
import { SELECT_MODE } from 'app/constants/table'
import { MULTI_ACTION_PANEL } from 'app/constants/subscriberCRM'
import useSelectGroup from 'app/main/subscribersV4/useSelectGroup'
import CollapsePanel from 'app/componentV4/Collapses/CollapsePanel'
import GroupSelectorContent from './components/GroupSelectorContent'
import CreateGroupForm from 'app/componentV4/CreateGroupForm/CreateGroupForm'
import useBulkAssignGroupHandlers from './useBulkAssignGroupHandlers'
import { useAccessControl } from 'app/auth/useAccessControl'
import { AccessType } from 'app/auth/accessControlService'
import PrimaryTooltip from 'app/componentV4/Tooltip/PrimaryTooltip'
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined'

const PAGE_LIMIT = 20

const BulkAssignGroupDialog = (props) => {
  const { formatMessage } = useIntl()
  const {
    isOpen,
    mode,
    subscriberIds = [],
    subscriberCount,
    value,
    onConfirm,
    onAssignGroups,
    onCloseDialog,
    params = {},
  } = props

  const [openPanel, setOpenPanel] = useState(MULTI_ACTION_PANEL.SELECT_EXISTING)
  const [loadingState, setLoadingState] = useState(null)
  const infoIconRef = useRef(null)

  // Check admin permission
  const { isGranted: hasAdminPermission } = useAccessControl([
    AccessType.ADMINISTRATION,
  ])

  const {
    state: { groups, selectedTags, isLoading, meta, isLoadmore },
    onFetchGroups,
    onDefaultValue,
    setSelectedTags,
    handleParentCheckboxChange,
    handleTagCheckboxChange,
    loadMore,
    onSearchChange,
    clearState,
    checkAllByGroup,
  } = useSelectGroup()

  // custom hook for handlers
  const { handleBulkAssignToExistingGroups, handleCreateAndBulkAssignGroup } =
    useBulkAssignGroupHandlers({
      mode,
      subscriberIds,
      subscriberCount,
      onAssignGroups,
      selectedTags,
      groups,
      params,
      onConfirm,
      onCloseDialog,
      onFetchGroups,
      setLoadingState,
      setOpenPanel,
    })

  const resetAccordionStates = () => {
    setOpenPanel(MULTI_ACTION_PANEL.SELECT_EXISTING)
  }

  useEffect(() => {
    if (isOpen) {
      onFetchGroups({
        groups,
        page: meta.currentPage,
        limit: PAGE_LIMIT,
        search: '',
      })
      onDefaultValue(value || [])
      resetAccordionStates()
    } else {
      clearState()
      resetAccordionStates()
    }
  }, [isOpen])

  // Styling related code
  // Styling helpers for dynamic dialog behavior
  const isAssignGroupExpanded = openPanel === MULTI_ACTION_PANEL.SELECT_EXISTING
  const bothCollapsed = openPanel === null

  const dialogClasses = {
    paper: clsx('w-screen flex flex-col', isAssignGroupExpanded && 'h-screen'),
    scrollPaper: clsx(
      isAssignGroupExpanded && 'overflow-hidden',
      bothCollapsed && '!overflow-hidden',
      !isAssignGroupExpanded && !bothCollapsed && 'overflow-visible',
    ),
    container: bothCollapsed && '!overflow-hidden',
  }

  const dialogContentClasses = clsx('px-9 pb-3 pt-0', {
    'flex flex-col flex-1 min-h-0 overflow-hidden': isAssignGroupExpanded,
    'overflow-hidden': bothCollapsed,
    'overflow-visible': !isAssignGroupExpanded && !bothCollapsed,
  })

  const innerContainerClasses = clsx(
    'flex flex-col',
    isAssignGroupExpanded && 'flex-1 min-h-0',
  )

  const assignGroupContainerClasses = clsx(
    'border-0',
    isAssignGroupExpanded && 'flex flex-col flex-1 min-h-0',
  )

  const assignGroupContentClasses = clsx(
    'px-0 sm:px-0 pt-0 py-0 sm:py-0',
    isAssignGroupExpanded && '!h-full flex flex-col flex-1 min-h-0',
  )

  return (
    <div>
      <Dialog
        open={isOpen}
        onClose={onCloseDialog}
        fullWidth
        maxWidth="sm"
        classes={dialogClasses}
      >
        <SelectGroupDialogHeader
          titleLocaleKey="create.assign.group.dialog.subscriber.count"
          titleValues={{ count: subscriberCount }}
          onCloseDialog={onCloseDialog}
        />

        <DialogContent className={dialogContentClasses}>
          <div className={innerContainerClasses}>
            {/* Create New Group Accordion - First, Collapsed by default - Admin Only */}
            <CollapsePanel
              open={
                hasAdminPermission &&
                openPanel === MULTI_ACTION_PANEL.CREATE_NEW
              }
              disabled={!hasAdminPermission}
              containerClassName="border-0 overflow-visible"
              headerClassName={clsx(
                'px-0 sm:px-0 text-16',
                !hasAdminPermission && 'cursor-not-allowed text-grey-100',
              )}
              contentClassName="px-0 sm:px-0 pt-0 py-0 sm:py-0 overflow-visible"
              title={
                !hasAdminPermission ? (
                  <PrimaryTooltip
                    title={formatMessage({
                      id: 'create.assign.group.dialog.create.group.tooltip',
                    })}
                    placement="top"
                    PopperProps={{
                      anchorEl: infoIconRef.current,
                      placement: 'top',
                    }}
                  >
                    <span className="flex items-center gap-2">
                      {formatMessage({
                        id: 'create.assign.group.dialog.create.group.tab',
                      })}
                      <InfoOutlinedIcon
                        ref={infoIconRef}
                        fontSize="small"
                        className="text-primary"
                      />
                    </span>
                  </PrimaryTooltip>
                ) : (
                  <span className="flex items-center gap-3">
                    {formatMessage({
                      id: 'create.assign.group.dialog.create.group.tab',
                    })}
                  </span>
                )
              }
              toggleCollapse={() => {
                if (!hasAdminPermission) {
                  return // Prevent toggle if no admin permission
                }
                if (openPanel === MULTI_ACTION_PANEL.CREATE_NEW) {
                  setOpenPanel(null)
                } else {
                  setOpenPanel(MULTI_ACTION_PANEL.CREATE_NEW)
                }
              }}
            >
              {hasAdminPermission ? (
                <CreateGroupForm
                  id="bulk-assign-group-dialog-create-group-form"
                  onSubmit={handleCreateAndBulkAssignGroup}
                  onCancel={onCloseDialog}
                  disabled={loadingState !== null}
                  containerClassName="p-0 pb-9"
                  submitButtonLabelId="subscriber.action.create.and.add"
                />
              ) : null}
            </CollapsePanel>

            <div className="border-t border-primary-350 mb-2"></div>

            {/* Select Existing Groups Accordion - Second, Expanded by default */}
            <CollapsePanel
              open={openPanel === MULTI_ACTION_PANEL.SELECT_EXISTING}
              containerClassName={assignGroupContainerClasses}
              headerClassName="px-0 sm:px-0 flex-shrink-0 text-16"
              contentClassName={assignGroupContentClasses}
              title={formatMessage({
                id: 'create.assign.group.dialog.exisiting.group',
              })}
              toggleCollapse={() => {
                if (openPanel === MULTI_ACTION_PANEL.SELECT_EXISTING) {
                  setOpenPanel(null)
                } else {
                  setOpenPanel(MULTI_ACTION_PANEL.SELECT_EXISTING)
                }
              }}
            >
              <div className="flex flex-col flex-1 min-h-0">
                <div className="flex flex-col flex-1 min-h-0 overflow-hidden">
                  <GroupSelectorContent
                    id="bulk-assign-group-dialog-group-selector-content"
                    groups={groups}
                    selectedTags={selectedTags}
                    isLoading={isLoading}
                    meta={meta}
                    isLoadmore={isLoadmore}
                    onSearchChange={onSearchChange}
                    handleParentCheckboxChange={handleParentCheckboxChange}
                    handleTagCheckboxChange={handleTagCheckboxChange}
                    checkAllByGroup={checkAllByGroup}
                    loadMore={loadMore}
                    setSelectedTags={setSelectedTags}
                  />
                </div>

                <SelectGroupDialogFooter
                  id="bulk-assign-group-dialog-select-group-dialog-footer"
                  isLoading={loadingState !== null}
                  confirmButtonDisabled={selectedTags.length === 0}
                  confirmButtonTextLocaleKey="subscriber.action.add.to.group"
                  onCloseDialog={onCloseDialog}
                  onConfirm={handleBulkAssignToExistingGroups}
                />
              </div>
            </CollapsePanel>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}

BulkAssignGroupDialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  mode: PropTypes.oneOf([SELECT_MODE.SELECTED, SELECT_MODE.ALL]).isRequired,
  subscriberIds: PropTypes.array, // Only used when mode="ids"
  subscriberCount: PropTypes.number.isRequired, // Count of subscribers to display
  value: PropTypes.array,
  onConfirm: PropTypes.func,
  onAssignGroups: PropTypes.func.isRequired, // Unified function: ({ subscriberIds?, tagIds, type }) => Promise
  onCloseDialog: PropTypes.func.isRequired,
  params: PropTypes.object, // Filter/search params for building query string
}

export default BulkAssignGroupDialog
